import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DayPilot, DayPilotSchedulerComponent } from "daypilot-pro-angular";
import { List } from 'immutable';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { BusEnum, ColorsEnum, GroupType, TransferStatus, activityAbbreviations, arrivalStatus, departureStatus } from '../common/enums';
import { GateStatus } from '../model/GateStatus';
import { Activity } from '../model/activity';
import { ActivityActorEvent } from '../model/activityActor-event';
import { DaypilotCoreEvent } from '../model/daypilot-core-event';
import { ArrivalStatus, DepartureStatus, LiveFlight } from '../model/live-flight';
import { LiveFlightEvent } from '../model/liveflight-event';
import { Location } from '../model/location';
import { StateMachineEventEnum } from '../model/stateMachineEvent';
import { ActivityActorService } from '../services/activityActor.service';
import { SettingsService } from '../services/settings.service';
import { SignalRService } from '../services/signalr.service';
import { TooltipService } from '../services/toolTip.service';
import { DateTimeUtils } from '../shared/datetimeutils';
import { TransferDialog } from '../transfer/transfer.dialog';
import { LoggerService } from '../services/logger.service';
import { LocationService } from '../services/location.service';
import { ISchedulerMediatorServiceClient, SchedulerMediatorService } from './scheduler-mediator.service';
import { LiveFlightService } from '../services/liveFlight.service';
import { BfsService } from '../services/bfs.service';
import { ActivatedRoute } from '@angular/router'
import { guardSchedulerUpdates } from './daypilot-update-guard';

@Component({
  selector: 'app-scheduler',
  templateUrl: './scheduler.component.html',
  styleUrls: ['./scheduler.component.css', './areas.css', './scheduler_8.css', './navigator_white.css']
})

@UntilDestroy()

export class SchedulerComponent implements AfterViewInit, OnInit, OnDestroy, ISchedulerMediatorServiceClient {

  public overflowStyle: string = "hidden";  //used for ngStyle for split-area components

  @ViewChild("scheduler", { static: true })
  scheduler: DayPilotSchedulerComponent;

  @HostListener('document:mouseup', ['$event'])
  mouseUp(event) { this.schedulerMediatorService.movingDone(); }

  allowCreateTransfer: boolean = true;

  /**
   * Constants
   **/
  // Resource definition for the main branches in the DayPilot view
  resourceGroups: any[] = [
    {
      name: "Transfers",
      id: "transfers",
      type: GroupType.Transfer,
      expanded: false,
      children: []
    },
    {
      name: "Flights",
      id: "flights",
      type: GroupType.Flights,
      expanded: true,
      children: []
    }
  ];

  readonly ActivityActiveStatus = "ACTIVE";
  readonly ActivityClosedStatus = "CLOSED";
  readonly UnplannedArrivalErrorMinutes = 15;
  readonly UnplannedArrivalWarningMinutes = 30;
  readonly UnplannedFlightWarningMinutes = 15;
  readonly UnplannedDepartureErrorMinutes = 15;
  readonly UnplannedDepartureWarningMinutes = 30;

  states: any[] = activityAbbreviations;
  transferText: string;
  private _events: DaypilotCoreEvent[] = [];
  activityActors: ActivityActorEvent[] = [];
  transferPoints: Location[];
  transferEvents: List<ActivityActorEvent>;

  plannedActivities: number;
  locations: Location[];

  transferModalRef: MatDialogRef<TransferDialog>;

  scheduleHeight: number = 500;
  private releaseUpdateGuard

  get events() {
    return this._events;
  }
  set events(value: DaypilotCoreEvent[]) {
    this._events = value;
  }

  public get showPlannedFlights() {
    return this.settings.showPlannedFlights;
  }

  public get showAllDrivers() {
    return this.settings.showAllDrivers;
  }

  public get showFinishedEvents() {
    return this.settings.showFinishedEvents;
  }

  public get showLandside() {
    return this.settings.showLandside;
  }

  config: any = {
    timeHeaders: [
    ],
    dynamicEventRendering: 'disabled',
    locale: this.settings.getCurrentLocale(),
    showNonBusiness: true,
    allowEventOverlap: false,
    autoScroll: "Disabled",
    businessBeginsHour: 0,
    businessEndsHour: 24,
    businessWeekends: true,
    cellDuration: 10,
    dragOutAllowed: true,
    scale: "Manual",
    timeline: [],
    days: 1,
    heightSpec: "Parent100Pct",
    startDate: new DayPilot.Date(),
    sortDirections: ["desc", "asc"],
    treeEnabled: true,
    rowHeaderWidthAutoFit: false,
    rowHeaderWidth: 210,
    onRowMoving: (args) => { this.onRowMoving(args); },
    showCurrentTime: false,
    snapToGrid: false,
    useEventBoxes: 'Never',
    crosshairType: 'Disabled',
    resources: this.resourceGroups,
    onBeforeResHeaderRender: (args) => {
      this.onBeforeResHeaderRender(args);
    },
    onBeforeRowHeaderRender: (args) => {
      args.row.areas = this.getResourceAreas(args);
    },
    onBeforeEventRender: (args) => {
      args.data.moveHDisabled = false;
      args.data.resizeDisabled = true;
      args.data.fontColor = '#000000';
      args.data.barColor = this.flightBarStatusColor(args.data);

      args.data.areas = this.getEventAreas(args.data);

      if (args.data.liveFlight) {
        if (args.data.liveFlight.isArrival) {
          args.data.backColor = ColorsEnum.Arrival;

        }
        else {
          if (args.data.liveFlight.isRemoteGate) {
            args.data.backColor = ColorsEnum.DepartureRemoteTerminal;
          }
          else {
            args.data.backColor = ColorsEnum.Departure;
          }

        }
      }
      else {
        args.data.backColor = "#F0F0F0";
      }
    },
    onEventMoving: (args) => {
      this.schedulerMediatorService.isMoving();
      if (args.external) console.log("external");
      args.allowed = false;
    },
    onBeforeCellRender: (args) => {
      args.cell.backColor = ColorsEnum.NonWorking;
    },
    onEventRightClick: (args) => {
      this.onEventRightClickFunction(args);
    },
    onRowFilter: args => {
      args.visible = false;

      // make sure showAllFlights is disabled since the button is no longer visible
      args.filter.showAllFlights = false;

      // User wants to show all flights
      if (args.filter.showAllFlights) {
        args.visible = true;

      } else if (args.row.tags) {
        if (args.row.data.type == GroupType.Flights) {
          if (args.filter.showPlannedFlights) {
            // Show the row if it's tagged as a flight requiring a bus or if the row should always be displayed
            if (args.row.tags.bus || args.row.tags.alwaysdisplay) {
              args.visible = true;
            }
          } else {
            // Show the row if it requires a bus or but is not planned
            if ((args.row.tags.plannedNumberOfBuses < args.row.tags.numberOfBuses && args.row.tags.bus) || args.row.tags.alwaysdisplay) {
              args.visible = true;
            }
          }
        }
        else {
          args.visible = true;
        }
      }
    },
    cssOnly: true,
    bubble: new DayPilot.Bubble({
      animation: "jump",
      showAfter: 500,
      hideAfter: 100,
      animated: false,
      onLoad: (args) => {
        args.html = this.htmlBubble(args);
        args.loaded();
      }
    })
  };

  liveFlightSubscription: Subscription;
  locationsSubscription: Subscription;
  createLiveFlightsSubscription: Subscription;
  updateLiveFlightsSubscription: Subscription;
  deleteLiveFlightSubscription: Subscription;
  gateStatusSubscription: Subscription;
  updateActivitySubscription: Subscription;
  deleteActivitySubscription: Subscription;
  pingSubscription: Subscription;

  constructor(
    private readonly liveFlightService: LiveFlightService,
    private readonly settings: SettingsService,
    private readonly activityActorService: ActivityActorService,
    private readonly toaster: ToastrService,
    private readonly tooltip: TooltipService,
    private readonly cdr: ChangeDetectorRef,
    private readonly logger: LoggerService,
    private readonly dialog: MatDialog,
    private readonly locationService: LocationService,
    private readonly schedulerMediatorService: SchedulerMediatorService,
    private readonly bfsService: BfsService,
    private readonly route: ActivatedRoute,
    private renderer: Renderer2) {

   
  }

  ngOnInit() {
    this.transferEvents = List<ActivityActorEvent>();
    this.states = activityAbbreviations.filter(x => x.legend == true);
    if (this.locationService.Locations) {
      this.setupTransferPoints(this.locationService.Locations);
    }

    this.locationsSubscription = this.locationService.locationsLoaded$.subscribe((result: Location[]) => {
      this.setupTransferPoints(result);
    });

  };

  ngAfterViewInit() {
    this.scheduler.config = this.config;
    this.releaseUpdateGuard = guardSchedulerUpdates(this.scheduler?.control);

    this.updateTimeLine(this.schedulerMediatorService.currentTimeLine);

    this.settings.toggleShowAllFlights$.subscribe(item => {
      this.applyFilter();
    });

    this.settings.toggleShowPlannedFlights$.subscribe(item => {
      this.applyFilter();
    });

    this.pingSubscription = this.bfsService.ping$.subscribe((gateStatus: GateStatus) => {
      try {
        let gate = this.getTransferResourceGroup().children.find(x => x.name === gateStatus.gate);
        if (gate) {
          gate.ping = gateStatus;
        }
      }
      catch (error) {
        this.logger.logError(error);
      }
    });

    this.gateStatusSubscription = this.bfsService.updateGateStatus$.subscribe((gates: GateStatus[]) => {
      try {
        gates.forEach(item => {
          let gate = this.getTransferResourceGroup().children.find(x => x.name === item.gate);
          if (gate) {
            gate.status = item.gateStatus;
          }
        });
      }
      catch (error) {
        this.logger.logError(error);
      }

    });

    this.deleteActivitySubscription = this.activityActorService.deleteActivity$.subscribe((activity: ActivityActorEvent) => {
      try {
        let index = this.activityActors.findIndex(x => x.activityActor.id == activity.activityActor.id);
        if (index >= 0) {
          this.activityActors.splice(index, 1);
          this.processActivityActor(activity);
        }
      }
      catch (error) {
        console.log(error);
        this.logger.logError(error);
      }
    });

    this.schedulerMediatorService.registerClient(this);

    this.initialLoad();
    this.route.data.subscribe(() => {
      const transferState = window.history.state['transfer'];
      if (transferState) {
        this.showTransfers();
      }
    });
    
  }

  ngOnDestroy() {

    if (this.liveFlightSubscription && !this.liveFlightSubscription.closed)
      this.liveFlightSubscription.unsubscribe();

    this.schedulerMediatorService.unregisterClient(this);
    this.releaseUpdateGuard();
  }

  
  private initialLoad() {

    let liveFlightEvents = this.schedulerMediatorService.liveFlights;
    this.events = liveFlightEvents;
    this.updateLiveFlightEvents(liveFlightEvents);

    this.activityActorService.activityActorLoaded$.subscribe(_ => {

      let liveFlightEvents = this.schedulerMediatorService.liveFlights;
      this.events = liveFlightEvents;
      this.updateLiveFlightEvents(liveFlightEvents);

      this.activityActors = this.activityActorService.activityActors;
      this.processActivityActor(null);
    });

    // this is to remove flights that has been planned
    if (this.activityActors && this.activityActors.length === 0 && this.activityActorService && this.activityActorService.activityActors) {
      this.activityActors = this.activityActorService.activityActors;
      this.processActivityActor(null);
    }
  }

  // mediator interface implementations start

  createLiveFlightEvents(liveFlights: LiveFlightEvent[]): void {
    this.updateLiveFlights(liveFlights);
  }

  updateLiveFlightEvents(liveFlights: LiveFlightEvent[]): void {
    console.log("Update liveflight");
    this.updateLiveFlights(liveFlights);
  }

  deleteLiveFlightEvents(liveFlights: LiveFlightEvent[]): void {
    this.deleteLiveFlights(liveFlights);
  }

  public onLiveFlightLoaded(liveFlights: LiveFlightEvent[]): void {
    this.initialLoad();
  }

  public updateActivityActorEvent(activityActorEvent: ActivityActorEvent): void {
    try {

      let index = this.activityActors.findIndex(x => x.activityActor.id == activityActorEvent.activityActor.id);
      if (index >= 0) {
        if (activityActorEvent.activityActor.state === StateMachineEventEnum.UnAssign) {
          this.setPlannedNumberOfBuses(activityActorEvent);
          return;
        } else {
          this.activityActors.splice(index, 1, activityActorEvent);
        }
        if (activityActorEvent.activityActor.actor.type == BusEnum.Transfer &&
          activityActorEvent.activityActor.state == StateMachineEventEnum.Cancel &&
          activityActorEvent.activityActor.modifiedBy == "BFS") {
          this.showConfirm("Transfer avbruten av " + activityActorEvent.activityActor.actor.name);
        }
      }
      else {
        this.activityActors.push(activityActorEvent);
      }


      this.processActivityActor(activityActorEvent);
    }
    catch (error) {
      this.logger.logError(error);
    }
  }

  public instanceName(): string {
    return "Scheduler";
  }

  public updateTimeLine(timeLine: any[]): void {

   

    this.config.timeline = timeLine;
    this.clearHistory();

    if (this.scheduler?.control != null && this.scheduler?.control != undefined) {
      this.scheduler.control.update();
    }
  }

  public updateNowMarker(date: DayPilot.Date) {

    // Update unplanned flights DayPilot control
    try {
      this.adjustTransferQueue(this.transferEvents.toArray());

      if (this.scheduler.control) {
        this.scheduler.control.update({
          separators: [{
            color: "red",
            layer: "AboveEvents",
            location: date
          }]
        });
        
      }
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }

  // Will disable new events
  // to not overwhelm daypilot with events
  public delayActionsDueToDropOfEvent() {

    this.renderer.addClass(document.body, 'event_hover_wait');
    this.scheduler.control.eventMoveHandling = "Disabled";

    setTimeout(() => {
      this.scheduler.control.eventMoveHandling = "Update"
      this.renderer.removeClass(document.body, 'event_hover_wait');
    }, 1000);

    
  }

  // mediator interface implementations end

  private deleteLiveFlights(liveFlights: LiveFlightEvent[]) {
    try {
      let changed = false;
      liveFlights.forEach((flight: LiveFlightEvent) => {
        flight.resource = flight.id;
        let index = this.events.findIndex(x => x.id === flight.id);
        if (index >= 0) {
          // delete
          this.events.splice(index, 1);
          this.removeLiveFlightResource(flight);
          changed = true;
        }
      });

      if (changed && this.scheduler?.control) {
        this.scheduler.control.update();
      }
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }
  
  showTransfers() {
    if (this.hasTransfers) {
      this.scheduler.control.setScrollY(0);
      let firstTransfer: ActivityActorEvent = this.transferEvents.sortBy(x => x.start).first();

      if (!firstTransfer) {
        return;
      }

      let date = new DayPilot.Date(firstTransfer.start).addMinutes(-20);
      this.scheduler.control.scrollTo(date);
    }
  }

  canCreateTransfer() {
    return this.allowCreateTransfer;
  }

  assignPlanned() {
    this.activityActorService.assignPlanned().then(reply => {

    }, (error) => {
      console.log(error)
      this.showError(error);
    });
  }

  setupTransferEvent(event) {
    try {
      event.start = new DayPilot.Date(event.start, true);
      event.end = new DayPilot.Date(event.end, true);
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }

  onRowMoving(args) {
    args.allowed = false;
  }

  setupTransferPoints(locations: Location[]) {
    try {
      this.locations = locations;
      this.transferPoints = this.locations.filter(x => x.type == "T");

      let transferGroup = this.getTransferResourceGroup();;
      // /*this.config.resources[0].children.push({*/
      this.transferPoints.forEach(transferPoint => {
        transferGroup.children.push({
          name: transferPoint.name,
          id: transferPoint.name,
          ping: null,
          status: transferPoint.status == "Open" ? TransferStatus.Open : TransferStatus.Close,
          online: false,
          type: GroupType.Transfer,
          tags: {
            alwaysdisplay: true
          }
        });
      });

      if (!locations || locations.length == 0) {
        this.logger.logEvent("LocationSubscription", "no locations.");
      }
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }

  canAssignPlanned() {
    return this.plannedActivities > 0;
  }

  onPlannedActivities(numberOfPlannedActivities: number) {
    this.plannedActivities = numberOfPlannedActivities;
  }

  onUpperScheduleHeightChanged(value: boolean) {
    if (this.scheduler.control) {
      this.scheduler.control.update();
    }
  }

  cancelTransfer(transferPoint) {
    this.bfsService.cancelTransfer(transferPoint).then(reply => {
      this.showInfo("Transfer avbruten.");
    }, (error) => {
      this.showError("Kunde inte avbryta transfer.");
    });
  }

  stateBgColor(activity) {
    let item = this.states.find(x => x.state == activity.state);
    return item.color;
  }

  createTransfer(event: MouseEvent) {
    const target = new ElementRef(event.currentTarget);
    // set this to prevent simultaneous range select event
    this.transferModalRef = this.dialog.open(TransferDialog, {
      disableClose: false,
      panelClass: 'transferDialog',
      closeOnNavigation: false, data:
      {
        "trigger": target,
        "transferPoint": "",
        "allowedTransferPoints": this.transferPoints
      }
    });

    this.transferModalRef.afterClosed().subscribe(result => {
      if (result) {

        this.bfsService.requestTransfer(result.transferPoint).then(reply => {
          this.showInfo("Transfer skapad.");
        }, (error) => {
          this.showError("Kunde inte skapa transfer.");
        });

      }
    })
  }

  resizeDaypilot() {
    //After angular-split has ended dragging resized both daypilots. It's possible to resize during dragging but has its costs
    this.scheduler.control.show();
  }

  // Left edge of the rendered timeline (falls back to settings before the first timeline is built)
  private get visibleWindowStart(): moment.Moment {
    const first = this.config.timeline?.[0]?.start;
    return first ? moment(first.toString()) : moment(this.settings.windowStart);
  }

  private isInWindow(event: DaypilotCoreEvent): boolean {
    if (!event?.end) return false;
    const end = moment(event.end.toString());
    return end.isValid() && end.isSameOrAfter(this.visibleWindowStart);
  }

  private filterLiveFlightEventsForWindow(events: DaypilotCoreEvent[]): DaypilotCoreEvent[] {
    return events.filter(x => this.isInWindow(x));
  }

  private filterOldLiveFlightEventsForWindow(events: DaypilotCoreEvent[]): DaypilotCoreEvent[] {
    return events.filter(x => !this.isInWindow(x));
  }

  clearHistory() {
    if (!this.events) return;

    const liveFlightEvents = this.events.filter((x: any) => x.objectType === "LiveFlightEvent");
    const oldFlights = this.filterOldLiveFlightEventsForWindow(liveFlightEvents) as LiveFlightEvent[];
    oldFlights.forEach(flight => {
      this.removeLiveFlightResource(flight);
      const eventIndex = this.events.findIndex(x => x.id == flight.id);
      if (eventIndex >= 0) {
        this.events.splice(eventIndex, 1);
      }
    });

    // Remove any flight resource that has no event
    const children = this.getFlightResourceGroup().children;
    const eventIds = new Set(this.events.map(e => e.id));
    for (let i = children.length - 1; i >= 0; i--) {
      if (!eventIds.has(children[i].id)) {
        children.splice(i, 1);
      }
    }

    if (this.scheduler?.control) {
      this.scheduler.control.update({ resources: this.resourceGroups, events: this.events });
    }
  }

  private setPlannedNumberOfBuses(activityActorEvent: ActivityActorEvent) {
    if (!activityActorEvent) return;

    let resource = this.getFlightResourceGroup().children.find(c => c.id === activityActorEvent.activityActor.actor.liveFlightID);
    if (!resource) {
      return;
    }

    let events = this.activityActors.filter(a => a.planned && a.activityActor.actor.liveFlightID === activityActorEvent.activityActor.actor.liveFlightID);
    resource.tags.plannedNumberOfBuses = events.length;
  }

  onBeforeResHeaderRender(args) {
    // Transfer gates
    if (args.resource.level == 1 && args.resource.type == "Transfer") {
      args.resource.backColor = this.isGateOnline(args.resource) ? "white" : "#E9CCCC";
    }

  }

  isGateOnline(gate): boolean {
    let online = false;
    if (gate.ping) {
      let then = moment(gate.ping.date);
      let now = moment(new Date());
      let diff = moment.duration(now.diff(then)).asSeconds();
      online = diff <= 60;
    }

    return online;
  }

  getResourceAreas(args) {
    try {
      let areas: any[] = [];
      let color = "";

      if (args.row.level == "0" && args.row.name == "Transfers") {
        let allOnline = true;
        let allOpen = true;
        this.getTransferResourceGroup().children.forEach(transfer => {
          let online = false;
          if (transfer.ping) {
            online = this.isGateOnline(transfer);
          }
          let open = transfer.status === TransferStatus.Open;
          transfer.online = online;

          if (!online) {
            allOnline = false;
          }
          if (!open) {
            allOpen = false;
          }
        });

        color = "yellow";
        if (allOpen && allOnline) {
          color = "#7AB800";
        }
        else if (!allOnline) {
          color = "#E9CCCC";
        }

        let allCheckedImage = '/assets/images/check.svg';
        if (!allOpen)
          allCheckedImage = '';

        let arrowsImageUrl = "<img style='margin-left:0px;width:13px;height:10px;margin-top: 3px;' src = " + allCheckedImage + " />";
        let arrows = "<div style='display: table-cell;float:left;width:18px;height:18px;background:" + color + "'>" + arrowsImageUrl + "</div>";
        let text = "<span style='display: table-cell; height:18px;color:black; height:18px;vertical-align: middle; '>Skifta alla</span>";
        let button = "<div style='display: table;width:90px;height:18px;'>" + text + arrows + "</div>";

        areas.push({
          right: 0, top: 7, html: "<button title='Öppna stängda gater, stäng öppna gater.' style='cursor:pointer;background:transparent;border-width:0px'> " + button + "</button>", onClick: (args) => {
            this.toggleAllGates();
          }
        });

      }
      else if (args.row.level == "1") {
        if (args.row.data && args.row.data.type == "Transfer") {
          let image = "";
          let checkcolor = "#EBEBEB";
          if (args.row.data.status == TransferStatus.Open) {
            image =
              "<img style='margin-left:0px;width:13px;height:10px;margin-top: 3px;' src='/assets/images/check.svg'  />";
            checkcolor = "#83BA23";
          }

          let gateStatus: GateStatus = args.row.data.ping;

          let gateStatusText = "";

          if (gateStatus !== null && gateStatus !== undefined && gateStatus.action !== "Unknown") {
            gateStatusText = gateStatus.action;
          }

          let toolTip = args.row.data.status == TransferStatus.Open ? "Stäng " + args.row.data.name : "öppna " + args.row.data.name;
          let divimage = "<div style='display: table-cell;float:left;width:18px;height:18px;background:" + checkcolor + "'>" + image + "</div>"
          let status = "<div style='background-color:" + color + ";font-family:Helvetica Neue Regular,Arial,Verdana,Tahoma;width:8px;height:9px;border-style:none;border-size:0px'></div>";
          let statusArea = { html: status, left: 10, top: 13, visibility: "Visible" };
          let message = "<div style='display: table-cell;float:left;width:94px;height:21px;margin-right: 1px; vertical-align: middle;font-size:10px;line-height: 11px;margin-top: 1px;'>" + gateStatusText + "</div>"
          areas.push(statusArea);
          areas.push({
            right: 5, top: 8, html: message + "<button title='" + toolTip + "' style='cursor:pointer;background:transparent;border-width:0px'> " + divimage + " </button>", onClick: (args) => {
              this.toggleGate(args.source.data);
            }

          });
        }
      }

      return areas;
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }

  toggleAllGates() {
    try {

      let gates: GateStatus[] = [];
      let open: string[] = [];
      let close: string[] = [];
      this.getTransferResourceGroup().children.forEach(gate => {
        if (gate.status === TransferStatus.Open) {
          close.push(gate.name);
        }
        else {
          open.push(gate.name);
        }
        gates.push(new GateStatus(gate.name, gate.status === TransferStatus.Open ? TransferStatus.Close : TransferStatus.Open));
      });

      this.bfsService.updateGateStatus(gates)
        .then(reply => {

          if (open.length > 0)
            this.showInfo("Gate " + open.join(",") + " har öppnats.");
          if (close.length > 0)
            this.showInfo("Gate " + close.join(",") + " har stängts.");
        }, (error) => {
          this.showError("Ett fel uppstod vid uppdatering");
          console.log("Something went wrong while toggling all gates", error);
        });
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }

  toggleGate(gate: Location) {
    try {
      let gateStatus = gate.status === TransferStatus.Open ? TransferStatus.Close : TransferStatus.Open;
      this.bfsService.updateGateStatus([new GateStatus(gate.name, gateStatus)])
        .then(reply => {
          let newStatus = (gateStatus === TransferStatus.Close) ? "stängts." : "öppnats";
          this.showInfo("Gate " + gate.name + " har " + newStatus);
        }, (error) => {
          this.showError("Ett fel uppstod vid uppdatering av gate " + gate.name);
          console.log("Something went wrong while updating gate " + gate.name, error);
        });
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }

  private fillDayPilotEventsList(allEvents: any[], flightResources: any[]) {
    try {

      let localEvents = [];

      // Add the transfer Events first
      if (this.transferEvents) {
        this.transferEvents.forEach(event => {
          this.setupTransferEvent(event);
          localEvents.push(event);
        });
      }

      // Then add flight events
      if (allEvents) {
        allEvents.forEach((event: any) => {
          if (!localEvents.find(x => x.id === event.id)) {
            if (event.objectType === "LiveFlightEvent") {
              localEvents.push(event);
            }
          }
        });
      }

      // Load Scheduler Events using the Direct API for performance
      const dp = this.scheduler.control;

      if (!dp) {
        console.log("Dp is null, ignore update of scheduler");
        return;
      }

      // flight resource children are modified in place by createFlightResources / removeLiveFlightResource
      dp.update({ resources: this.resourceGroups, events: localEvents });
      this.events = localEvents;

      this.cdr.detectChanges();
    }
    catch (error) {
      this.logger.logError(error);
    }
  }

  private createTransferQueue() {
    this.transferEvents = List<ActivityActorEvent>();
    // Filter transfers so only non-assigned/in-progress are shown
    let transferActivitiesEvents = this.activityActors.filter(r => r.IsTransfer && r.activityActor.state === StateMachineEventEnum.Create);

    // Loop thru each event and set the id to the transfer point so DayPilot can match it with the correct resource
    transferActivitiesEvents.forEach(event => {
      let newEvent = new ActivityActorEvent(event.id,
        event.activityActor.actor.name,
        event.start.toDate(),
        event.end.toDate(),
        event.activityActor.actor.description,
        event.activityActor);
      if (this.transferEvents.findIndex(t => t.id === newEvent.id) == -1) {
        this.transferEvents = this.transferEvents.push(newEvent);
      }

    });
    let onGoingTransfers = this.transferEvents.filter(x => x.IsTransfer).toArray();
    this.adjustTransferQueue(onGoingTransfers);
  }

  processActivityActor(event: ActivityActorEvent) {
    try {

      this.setPlannedNumberOfBuses(event);

      this.createTransferQueue();

      const events = this.cloneEvents();
      this.fillDayPilotEventsList(events, []);
    }
    catch (error) {
      this.logger.logError(error);
    }
  }

  private cloneEvents(): DaypilotCoreEvent[] {
    // clone
    const allEvents: DaypilotCoreEvent[] = [];
    this.events.forEach(val => {
        allEvents.push(Object.assign({}, val))
    });

    return allEvents;
  }

  updateLiveFlights(flights: LiveFlightEvent[]) {
    try {
      // Final start/end + resource id must be set before the window check
      flights.forEach(flight => this.processFlightEvent(flight));

      const visibleFlights = this.filterLiveFlightEventsForWindow(flights) as LiveFlightEvent[];
      const hiddenFlights = this.filterOldLiveFlightEventsForWindow(flights) as LiveFlightEvent[];

      const allEvents = this.cloneEvents();

      // Moved out of the window: drop event and resource
      hiddenFlights.forEach(flight => {
        const index = allEvents.findIndex(x => x.id == flight.id);
        if (index >= 0) {
          allEvents.splice(index, 1);
        }
        this.removeLiveFlightResource(flight);
      });

      visibleFlights.forEach(flight => {
        const index = allEvents.findIndex(x => x.id == flight.id);
        if (index >= 0) {
          allEvents[index] = flight;
        } else {
          allEvents.push(flight);
        }
      });

      const flightResources = this.createFlightResources(visibleFlights);

      this.fillDayPilotEventsList(allEvents, flightResources);
      this.applyFilter();
    }
    catch (error) {
      this.logger.logError(error);
    }
  }

  processFlightEvent(flight: LiveFlightEvent): LiveFlightEvent {
    try {
      
      flight.resource = flight.id;
      this.setupFlightEvent(flight);
      return flight;
    }
    catch (error) {
      this.logger.logError(error);
    }
  }

  removeLiveFlightResource(flightEvent: LiveFlightEvent) {
    let index = this.getFlightResourceGroup().children.findIndex(x => x.id == flightEvent.resource);
    if (index >= 0) {
      this.getFlightResourceGroup().children.splice(index, 1);
    }
  }

  private getTransferResourceGroup(): any {
    return this.resourceGroups.find(r => r.type === GroupType.Transfer);
  }

  private getFlightResourceGroup(): any {
    return this.resourceGroups.find(r => r.type === GroupType.Flights);
  }

  private createFlightResources(flightEvents: LiveFlightEvent[]) {
    try {
      let flightResourceGroup = this.getFlightResourceGroup();
      let flightResources: any[] = flightResourceGroup.children;

      flightEvents.forEach((flightEvent) => {
        let activitiesEvent = null;

        // Mark the flight as planned if a bus assignment is already made
        if (flightEvent.liveFlight.bus) {

          // Mark as unplanned if found
          activitiesEvent = this.activityActors.filter(e => e.IsFlight &&
            e.activityActor.actor.liveFlightID === flightEvent.liveFlight.id &&
            ((e.activityActor.status === this.ActivityActiveStatus) ||
              (e.activityActor.status === this.ActivityClosedStatus && e.activityActor.state === StateMachineEventEnum.Final)));
        }

        let resource = {
          id: flightEvent.resource,
          name: flightEvent.liveFlight.flightNumber,
          type: GroupType.Flights,
          tags: {
            bus: flightEvent.liveFlight.bus,
            start: flightEvent.start,
            numberOfBuses: flightEvent.liveFlight.numberOfBuses,
            plannedNumberOfBuses: activitiesEvent ? activitiesEvent.length : 0
          }
        };

        // remove resouce if it exists
        let index = flightResources.findIndex(x => x.id == flightEvent.resource);
        if (index >= 0) {
          flightResources.splice(index, 1);
        }


        if (flightEvent.liveFlight.bus && this.isInWindow(flightEvent)) {
          // insert resource
          let pos = flightResources.findIndex(x => x.tags.start > flightEvent.start);
          if (pos >= 0) {
            flightResources.splice(pos, 0, resource);
          }
          else {
            flightResources.push(resource);
          }
        }
      });

      return flightResources;

    }
    catch (error) {
      this.logger.logError(error);
    }
  }

  hasTransfers() {

    let transfers = this.transferEvents ? this.transferEvents.count() > 0 : false;
    this.transferText = transfers ? "Transfers" : "Inga transfers";
    return transfers;
  }

  onEventRightClickFunction(args) {
    this.createContextMenu(args);
  }

  createContextMenu(args) {
    try {
      let event: ActivityActorEvent = args.e.data;
      let flightEvent: LiveFlightEvent = args.e.data;
      let activity: Activity = event.activityActor;

      let flight = args.e.data.liveFlight;

      let contextMenu: DayPilot.Menu;
      let etaLabel: string = "Ankomst (min)";
      let busLabel: string = "Antal bussar";

      contextMenu = new DayPilot.Menu({
        items: [

        ]
      });

      if (!flight) {
        contextMenu.items.push({
          text: "Avbryt", disabled: false, onClick: (args) => {
            this.cancelTransfer(activity.id);

          }
        });

        contextMenu.items.push(
          {
            text: etaLabel, disabled: false, onClick: (args) => { }
          });
      } else {
        contextMenu.items.push(
          {
            text: busLabel, disabled: false, onClick: (args) => { }
          });
      }

      let etaItem = contextMenu.items.find(x => x.text == etaLabel);
      if (etaItem) {
        etaItem.items = [];
        let eta = ["5", "10", "15", "20", "25"];
        eta.forEach((min: string) => {
          etaItem.items.push({ text: min, disabled: event.activityActor.actor.etaTransfer == min, onClick: (args) => { this.changeEta(event, min) } });
        });
      }
      let busItem = contextMenu.items.find(x => x.text == busLabel);
      if (busItem) {
        busItem.items = [];
        let buses = ["1", "2", "3", "4", "5"];
        buses.forEach((bus: string) => {
          busItem.items.push({ text: bus, disabled: flight.numberOfBuses == bus, onClick: (args) => { this.changeBuses(flightEvent, bus) } });
        });
      }

      if (contextMenu) {
        contextMenu.show();
      }
    }
    catch (error) {
      this.logger.logError(error);
    }
  }

  changeEta(event: ActivityActorEvent, min: string) {
    this.bfsService.updateTransferEta(event.activityActor.actorID, min)
      .then(reply => {
        this.showInfo('Transfern har uppdaterats.');
      }, (error) => {
        this.showError("Ett fel uppstod vid uppdateringen!");
        console.log("Something went wrong while updating transfer eta:", error);
        this.logger.logError(error);
      });
  }

  changeBuses(event: LiveFlightEvent, buses: string) {
    this.liveFlightService.setNumberOfBuses(event.liveFlight.id, buses)
      .then(reply => {
        this.showInfo('Antal bussar har uppdaterats.');
      }, (error) => {
        this.showError("Ett fel uppstod vid uppdateringen!");
        console.log("Something went wrong while updating required buses:", error);
        this.logger.logError(error);
      });
  }

  setupFlightEvent(event) {
    try {
      let departure = !event.liveFlight.isArrival;
      let arrival = event.liveFlight.isArrival;

      // add properties lost in translation
      if (arrival) {
        event.liveFlight.arrivalStatusExpanded = JSON.parse(event.liveFlight.arrivalStatus) as ArrivalStatus;
      }
      if (departure) {
        event.liveFlight.departureStatusExpanded = JSON.parse(event.liveFlight.departureStatus) as DepartureStatus;
      }

      let pax = this.tooltip.getNumberOfPassengers(event.liveFlight);
      let paxText = " P:" + pax;
      let startLocation: any = "";
      let endLocation: any = "";

      let duration = DateTimeUtils.calculateDurationNumber(event.start, event.end);

      let gate = "?";
      let stand = "?";

      if (event.liveFlight.gate !== undefined && event.liveFlight.gate !== null && event.liveFlight.gate !== "null") {
        gate = event.liveFlight.gate;
      }

      if (event.liveFlight.stand !== undefined && event.liveFlight.stand !== null && event.liveFlight.stand !== "null") {
        stand = event.liveFlight.stand;
      }

      if (departure) {
        startLocation = "G:" + gate;
        endLocation = "S:" + stand;

        // Departures shall appear 30 min before probable time
        event.start = new DayPilot.Date(event.liveFlight.probableTime, true).addMinutes(-30);
        event.end = new DayPilot.Date(event.liveFlight.probableTime, true).addMinutes(duration).addMinutes(-30);

      } else if (arrival) {
        startLocation = "S:" + stand;
        endLocation = "G:" + gate;

        event.start = new DayPilot.Date(event.liveFlight.probableTime, true);
        event.end = new DayPilot.Date(event.liveFlight.probableTime, true).addMinutes(duration);
      }

      let statusText;
      if (arrival) {
        statusText = " ST:" + arrivalStatus(event.liveFlight.arrivalStatusExpanded.flightLegStatus.toString().toUpperCase());
      }
      else {
        statusText = " ST:" + departureStatus(event.liveFlight.departureStatusExpanded.gateAction);
      }

      event.html = "<div style='margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis'>" + event.text + statusText + "<br/>" + startLocation + ":  " + endLocation + "<br/>" + paxText;
      event.height = 48;
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
    }
  }

  // Method that applies the chosen filter(s)
  applyFilter() {
    if (this.scheduler.control && this.scheduler.control.rows && this.settings) {
      this.scheduler.control.rows.filter(this.settings.settings);
    }
  }

  sortByStartTimeFunction(input) {
    return input.sort((a, b) => {
      if (a.start < b.start)
        return -1;
      else if (a.start > b.start)
        return 1;
      else
        return 0;
    });
  }



  editClosed(args) {
    if (args.result) {
      this.scheduler.control.message("Uppdaterad");
    }
  }

  // Determine the icon to be displayed on the event placeholder
  getEventAreas(event) {

    try {
      let areas = [];
      let icon: string = "";
      let color = "";
      // Display correct icon for flights
      if (event.liveFlight) {
        let flight: LiveFlight = (<LiveFlightEvent>event).liveFlight;
        let arrival = flight.isArrival;
        if (arrival) {
          icon = "arrow_downward";
        }
        else {
          icon = "arrow_upward";
        }

        let resource = this.getFlightResourceGroup().children.find(x => x.id == event.resource);
        if (!resource) {
          //console.log("could not find resource: " + event.resource);
        }
        else if (resource.tags.plannedNumberOfBuses < resource.tags.numberOfBuses && resource.tags.numberOfBuses > 1) {
          let busArea = { html: "<div style='background-color:#7AB800;color:white'>" + "&nbsp" + resource.tags.plannedNumberOfBuses + " (" + resource.tags.numberOfBuses + ")&nbsp </div>", right: 1, top: 32, visibility: "Visible" };
          areas.push(busArea);
        }
      }
      else {
        // Or bus icon if a transfer
        icon = "directions_bus";

        if (event.activityActor.actor.etaTransfer === null || event.activityActor.actor.etaTransfer === undefined) {
          let transferArea = { html: "<div style='color:" + color + "'><b>?</b></div>", right: 25, top: 10, visibility: "Visible" };
          areas.push(transferArea);
        }
        else {
          let estimated = moment(event.activityActor.actor.etaUpdated);
          estimated = estimated.add(event.activityActor.actor.etaTransfer, 'minutes');
          let now = moment(new Date());
          let remainingMinutes = moment.duration(estimated.diff(now)).asMinutes().toFixed(0);
          let diff = (estimated > now && remainingMinutes != "0") ? remainingMinutes : "1";
          color = (estimated > now && remainingMinutes != "0") ? "black" : "red";
          let transferArea = { html: "<div style='color:" + color + "'><b>" + diff + "</b></div>", right: 25, top: 10, visibility: "Visible" };
          areas.push(transferArea);
        }


      }

      areas.push({ html: "<i class='material-icons'>" + icon + "</i>", right: 0, top: 6, visibility: "Visible" });
      return areas;
    }
    catch (error) {
      console.log(error);
      this.logger.logError(error);
      return "";
    }
  }

  // Determine the header color of the unplanned flights
  flightBarStatusColor(event) {
    // Always return error color for transfers
    if (event.activityActor && event.activityActor.actor.type === BusEnum.Transfer) {
      return ColorsEnum.UnplannedFlightErrorHeader;
    }

    let flight: LiveFlight = (<LiveFlightEvent>event).liveFlight;
    let arrival = flight.isArrival;

    let now = Date.now();
    let errorMinutes = arrival ? this.UnplannedArrivalErrorMinutes : this.UnplannedDepartureErrorMinutes;
    let warningMinutes = arrival ? this.UnplannedArrivalWarningMinutes : this.UnplannedDepartureWarningMinutes;
    let errorTime = Date.now() + (errorMinutes * 60 * 1000);
    let warningTime = Date.now() + (warningMinutes * 60 * 1000);
    let eventstart = event.start.toDateLocal().getTime();

    if (eventstart <= errorTime) {
      return ColorsEnum.UnplannedFlightErrorHeader;
    }
    else if (eventstart > now && eventstart <= warningTime) {
      return ColorsEnum.UnplannedFlightWarningHeader;
    }
    else {
      return ColorsEnum.UnplannedFlightHeader;
    }
  }

  sortActivities(input: any): any {
    return input.sort((a: ActivityActorEvent, b: ActivityActorEvent) => {
      let aStart = new DayPilot.Date(a.activityActor.updatedAt, true);
      let bStart = new DayPilot.Date(b.activityActor.updatedAt, true);
      if (aStart < bStart)
        return -1;
      else if (aStart > bStart)
        return 1;
      else
        return 0;
    });
  }

  adjustTransferQueue(transfers: ActivityActorEvent[]) {
    if (transfers === undefined || transfers === null) return;
        
    let startTime = new DayPilot.Date();
    let index = 0;
    transfers.forEach(transfer => {
      if (!transfer.IsTransfer) {
        transfer.start = transfer.start.addMilliseconds(1); //touch to get event dirty
      } else {
        transfer.start = startTime.addMinutes(index * transfer.activityActor.durationMinutes);
        transfer.end = transfer.start.addMinutes(transfer.activityActor.durationMinutes);
        index++;
      }
    });

    // only redraw if events exists
    if (transfers.length > 0 && this.scheduler.control)
      this.scheduler.control.update();

  }

  // Create the HTML needed when displaying the popup info bubbles
  htmlBubble(args): string {
    let event = args.source.data;
    if (event.liveFlight) {
      return this.tooltip.createLiveFlightBubbleText(event, null, this.config, args.source.data.activityActor)

    } else if (event instanceof ActivityActorEvent) {
      let location = args.source.data.activityActor.actor.type == BusEnum.Transfer ?
        this.locations.find(x => x.type == "T" && x.name == args.source.data.activityActor.startLocation.name) :
        null;
      return this.tooltip.createAdminBubbleText(args.source.data.activityActor, this.config, location);
    }

    return "";
  }

  showConfirm(message: string) {
    this.toaster.warning(message, "Bekräfta", {
      tapToDismiss: true,
      disableTimeOut: true
    });
  }

  showError(message: string) {
    this.toaster.error(message);
  }

  showInfo(message: string) {
    this.toaster.info(message);
  }

  togglePlannedFlights() {
    this.settings.toggleShowPlannedFlights();
  }

  toggleAllDrivers() {
    this.settings.toggleShowAllDrivers();
  }

  toggleFinishedEvents() {
    this.settings.toggleShowFinishedEvents();
  }

  toggleLandside() {
    this.settings.toggleShowLandside();
  }
}
